import React, { useState } from 'react';
import { Table, Button, Card, Row, Col, Select, Tag, Tooltip, Modal, Divider, Badge } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import './TrainingRequestReview.scss';

const { Option } = Select;

// Mock Data for Training Requests
const requestsData = [
  { id: 1, requester: 'John Doe', team: 'Marketing', training: 'Advanced SEO Techniques', status: 'Pending', priority: 'High', submitted: '2024-09-10', estimatedCost: 2000, departmentBudget: 10000, budgetAlignment: true, comments: 'Urgent training for the marketing team.' },
  { id: 2, requester: 'Jane Smith', team: 'Sales', training: 'Salesforce Masterclass', status: 'Approved', priority: 'Medium', submitted: '2024-09-05', estimatedCost: 1500, departmentBudget: 8000, budgetAlignment: true, comments: 'Key for improving CRM knowledge.' },
  { id: 3, requester: 'Sam Green', team: 'HR', training: 'Employee Conflict Resolution', status: 'Rejected', priority: 'Low', submitted: '2024-09-01', estimatedCost: 2000, departmentBudget: 1200, budgetAlignment: false, comments: 'Could be postponed.' },
  { id: 4, requester: 'Alice Brown', team: 'IT', training: 'AWS Cloud Solutions', status: 'Pending', priority: 'High', submitted: '2024-09-12', estimatedCost: 3000, departmentBudget: 12000, budgetAlignment: true, comments: 'Necessary for upcoming cloud migration project.' },
];

const TrainingRequestsReview = () => {
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
  };

  const showDetailsModal = (request) => {
    setSelectedRequest(request);
    setIsModalVisible(true);
  };

  const handleApprove = (request) => {
    if (!request.budgetAlignment) {
      Modal.error({
        title: 'Approval Failed',
        content: `This request exceeds the department budget for ${request.team}.`,
      });
      return;
    }

    Modal.confirm({
      title: 'Approve Training Request',
      content: `Are you sure you want to approve the training request for ${request.training} by ${request.requester}?`,
      onOk() {
        // Logic to approve request
      },
    });
  };


  const handleReject = (request) => {
    Modal.confirm({
      title: 'Reject Training Request',
      content: `Are you sure you want to reject the training request for ${request.training} by ${request.requester}?`,
      onOk() {
        // Logic to reject request
      },
    });
  };

  const columns = [
    { title: 'Requester', dataIndex: 'requester', key: 'requester' },
    { title: 'Team', dataIndex: 'team', key: 'team' },
    { title: 'Training', dataIndex: 'training', key: 'training' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: 'Pending', value: 'Pending' },
        { text: 'Approved', value: 'Approved' },
        { text: 'Rejected', value: 'Rejected' },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => {
        let color = 'gray';
        if (status === 'Approved') color = 'green';
        if (status === 'Rejected') color = 'red';
        if (status === 'Pending') color = 'gold';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => {
        let color = priority === 'High' ? 'red' : priority === 'Medium' ? 'orange' : 'green';
        return <Badge color={color} text={priority} />;
      },
    },
    { title: 'Submitted', dataIndex: 'submitted', key: 'submitted' },
    {
      title: 'Actions',
      key: 'actions',
      render: (text, record) => (
        <div className="action-buttons">
          <Tooltip title="View Details">
            <Button
              icon={<InfoCircleOutlined />}
              onClick={() => showDetailsModal(record)}
            />
          </Tooltip>
          <Tooltip title="Approve">
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => handleApprove(record)}
              disabled={record.status !== 'Pending'}
            />
          </Tooltip>
          <Tooltip title="Reject">
            <Button
              type="danger"
              icon={<CloseCircleOutlined />}
              onClick={() => handleReject(record)}
              disabled={record.status !== 'Pending'}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const filteredRequests = statusFilter === 'All'
    ? requestsData
    : requestsData.filter((request) => request.status === statusFilter);

  return (
    <div className="training-requests-review">
      {/* Filter Section */}
      <Row gutter={16} className="filter-section">
        <Col span={6}>
          <Select
            defaultValue="All"
            onChange={handleStatusFilterChange}
            style={{ width: '100%' }}
          >
            <Option value="All">All Requests</Option>
            <Option value="Pending">Pending</Option>
            <Option value="Approved">Approved</Option>
            <Option value="Rejected">Rejected</Option>
          </Select>
        </Col>
      </Row>

      <Divider />

      {/* Training Requests Table */}
      <Row gutter={16}>
        <Col span={24}>
          <Card title="Training Requests" className="table-card">
            <Table columns={columns} dataSource={filteredRequests} rowKey="id" />
          </Card>
        </Col>
      </Row>

      {/* Request Details Modal */}
      <Modal
        title={`Training Request Details - ${selectedRequest?.training}`}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        className="request-details-modal"
      >
        {selectedRequest && (
          <div>
            <p><strong>Requester:</strong> {selectedRequest.requester}</p>
            <p><strong>Team:</strong> {selectedRequest.team}</p>
            <p><strong>Training:</strong> {selectedRequest.training}</p>
            <p><strong>Submitted on:</strong> {selectedRequest.submitted}</p>
            <p><strong>Estimated Cost:</strong> ${selectedRequest.estimatedCost}</p>
            <p><strong>Department Budget:</strong> ${selectedRequest.departmentBudget}</p>
            <p>
              <strong>Budget Alignment:</strong> {selectedRequest.budgetAlignment ? (
                <Tag color="green">Aligned</Tag>
              ) : (
                <Tag color="red">Not Aligned</Tag>
              )}
            </p>
            <p><strong>Priority:</strong> <Badge color={selectedRequest.priority === 'High' ? 'red' : selectedRequest.priority === 'Medium' ? 'orange' : 'green'} text={selectedRequest.priority} /></p>
            <p><strong>Comments:</strong> {selectedRequest.comments}</p>
          </div>
        )}
      </Modal>

    </div>
  );
};

export default TrainingRequestsReview;
